import React, { useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';

// Quick presets for "auto-expire this reminder after..." -- each maps to a
// function computing the actual expiry Date from now, since "end of day"
// and "tomorrow" depend on the current moment rather than a fixed offset.
const EXPIRY_PRESETS = [
  { key: 'none', label: 'No expiry (manual end only)', compute: () => null },
  { key: '1h', label: '1 hour', compute: () => new Date(Date.now() + 60 * 60 * 1000) },
  { key: '4h', label: '4 hours', compute: () => new Date(Date.now() + 4 * 60 * 60 * 1000) },
  {
    key: 'eod', label: 'End of day', compute: () => {
      const d = new Date();
      d.setHours(23, 59, 0, 0);
      return d;
    },
  },
  {
    key: 'tomorrow', label: 'Tomorrow morning', compute: () => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      d.setHours(9, 0, 0, 0);
      return d;
    },
  },
  { key: 'custom', label: 'Custom date/time...', compute: null },
];

// <input type="datetime-local"> needs 'YYYY-MM-DDTHH:mm' in LOCAL time --
// toISOString() would shift it to UTC and show the wrong time back to the
// admin, so this builds the string from local getters instead.
const toDatetimeLocalValue = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * Admin-only: post a new pinned reminder banner (see PriorityAlertBanner).
 * Shows for every employee until each dismisses it individually, an admin
 * ends it for everyone, or (if an expiry was set) it passes on its own.
 */
const PostAlertModal = ({ onClose, onPosted }) => {
  const [message, setMessage] = useState('');
  const [expiryPreset, setExpiryPreset] = useState('none');
  const [customExpiry, setCustomExpiry] = useState('');
  const [posting, setPosting] = useState(false);

  const handlePresetChange = (key) => {
    setExpiryPreset(key);
    if (key === 'custom' && !customExpiry) {
      setCustomExpiry(toDatetimeLocalValue(new Date(Date.now() + 60 * 60 * 1000)));
    }
  };

  const handlePost = async () => {
    if (!message.trim()) { toast.error('A message is required.'); return; }

    let expiresAt = null;
    if (expiryPreset === 'custom') {
      if (!customExpiry) { toast.error('Pick a custom expiry date/time.'); return; }
      expiresAt = new Date(customExpiry).toISOString();
    } else {
      const preset = EXPIRY_PRESETS.find((p) => p.key === expiryPreset);
      const computed = preset?.compute?.();
      expiresAt = computed ? computed.toISOString() : null;
    }

    try {
      setPosting(true);
      await api.createAlert(message.trim(), expiresAt);
      toast.success('Reminder posted.');
      onPosted?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not post the reminder.');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Post a reminder</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <p style={{ fontSize: '0.8rem', color: '#aab7c4' }}>
          Shows as a banner for every employee until each person dismisses it, or you end it for everyone.
        </p>

        <textarea
          className="form-textarea" style={{ minHeight: 90 }}
          value={message} onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g. Today's priority is order 1540"
          maxLength={500}
        />

        <div className="form-group" style={{ marginTop: '0.75rem' }}>
          <label className="form-label" htmlFor="alert-expiry">Auto-expire</label>
          <select
            id="alert-expiry" className="form-select"
            value={expiryPreset} onChange={(e) => handlePresetChange(e.target.value)}
          >
            {EXPIRY_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          {expiryPreset === 'custom' && (
            <input
              type="datetime-local" className="form-input" style={{ marginTop: '0.5rem' }}
              value={customExpiry} onChange={(e) => setCustomExpiry(e.target.value)}
            />
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={posting}>Cancel</button>
          <button type="button" className="btn-primary" onClick={handlePost} disabled={posting}>
            {posting ? 'Posting...' : 'Post reminder'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostAlertModal;
